import React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import AnimatedNumber from "@/components/AnimatedNumber";

/**
 * Inline SVG sparkline. Renders only when 2+ points are provided.
 */
const Sparkline = ({ points, width = 84, height = 28, color = "#2EB4AC" }) => {
    if (!points || points.length < 2) return null;
    const pad = 2;
    const vals = points.map((p) => (typeof p === "number" ? p : p.value || 0));
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max - min || 1;
    const step = (width - pad * 2) / (points.length - 1);
    const coords = vals.map((v, i) => {
        const x = pad + i * step;
        const y = pad + (height - pad * 2) * (1 - (v - min) / range);
        return [x, y];
    });
    const linePath = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
    const areaPath = `${linePath} L${coords[coords.length - 1][0].toFixed(1)},${height - pad} L${coords[0][0].toFixed(1)},${height - pad} Z`;
    const last = coords[coords.length - 1];
    const gradId = `spark-${color.replace("#", "")}-${Math.random().toString(36).slice(2, 6)}`;
    return (
        <svg width={width} height={height} className="shrink-0">
            <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.25" />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d={areaPath} fill={`url(#${gradId})`} />
            <path d={linePath} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx={last[0]} cy={last[1]} r="2.5" fill="white" stroke={color} strokeWidth="1.5" />
        </svg>
    );
};

/**
 * StatCard with optional sparkline, MoM delta chip, and click-to-drill.
 *
 * Props:
 *   icon, label, value, color (icon badge bg), testid
 *   trend: [n1, n2, ...] numeric array for sparkline (min 2 values)
 *   delta: { value, percent, inverted? } — inverted=true means higher is worse (attendance)
 *   hint: small caption below the value
 *   onClick: opens drill-down
 */
const StatCard = ({ icon: Icon, label, value, color = "bg-slate-100 text-slate-700", testid, trend, delta, hint, onClick }) => {
    const clickable = !!onClick;
    let deltaChip = null;
    if (delta && typeof delta.value === "number" && delta.value !== 0) {
        const up = delta.value > 0;
        // For inverted metrics (attendance issues), "up" is bad
        const negative = delta.inverted ? up : !up;
        const chipCls = delta.value === 0
            ? "bg-slate-50 text-slate-500 border-slate-200"
            : negative
            ? "bg-red-50 text-red-700 border-red-100"
            : "bg-green-50 text-green-700 border-green-100";
        const ChipIcon = up ? TrendingUp : TrendingDown;
        deltaChip = (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${chipCls}`}>
                <ChipIcon className="h-3 w-3" />
                {up ? "+" : ""}{delta.value}
                {typeof delta.percent === "number" && ` (${delta.percent > 0 ? "+" : ""}${delta.percent}%)`}
            </span>
        );
    } else if (delta && delta.value === 0) {
        deltaChip = (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border bg-slate-50 text-slate-500 border-slate-200">
                <Minus className="h-3 w-3" /> —
            </span>
        );
    }

    return (
        <div
            data-testid={testid}
            onClick={onClick}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}
            className={`group bg-white border border-slate-200 rounded-lg shadow-sm p-6 transition-all ${
                clickable ? "cursor-pointer hover:border-[#2EB4AC] hover:shadow-md" : ""
            }`}
        >
            <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
                    <div className="mt-2 flex items-baseline gap-2">
                        <div className="font-heading text-3xl font-bold text-slate-900 tabular-nums">
                            {typeof value === "number" ? <AnimatedNumber value={value} /> : value}
                        </div>
                        {deltaChip}
                    </div>
                    {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
                </div>
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
                    {Icon && <Icon className="h-4 w-4" />}
                </div>
            </div>
            {trend && trend.length >= 2 && (
                <div className="mt-3 flex items-center justify-between">
                    <Sparkline points={trend} />
                    {clickable && (
                        <span className="text-[10px] uppercase tracking-wider text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
                            View details →
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default StatCard;
